import React from 'react'
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl'
import { Link } from 'react-router-dom'
import { firstNameFromDisplayName } from '../../../utils/displayName'
import { hasHealthRecordsConnectionNeedingAttention } from '../../hrc/connectionAttention'
import type { HrcConnectSubject } from '../../hrc/types'
import type { OutstandingSecondaryConsentItem } from '../hooks/useOutstandingSecondaryConsents'

type Props = {
  items: OutstandingSecondaryConsentItem[]
  connectSubjects: HrcConnectSubject[]
}

const buttonClasses =
  'inline-flex shrink-0 items-center justify-center rounded-full bg-[#006045] px-5 py-3 text-sm font-semibold text-white shadow-xs transition hover:bg-[#004d37] focus:ring-2 focus:ring-[#006045]/20 focus:outline-none'

function getHealthRecordsLabel(intl: IntlShape, subjects: HrcConnectSubject[]): string {
  const hasSelf = subjects.some(({ role }) => role === 'SELF')
  const child = subjects.find(({ role }) => role === 'PROBAND')
  const firstName = firstNameFromDisplayName(child?.name)

  if (child && hasSelf) {
    return firstName
      ? intl.formatMessage(
          {
            id: 'home.outstandingItems.healthRecords.forChildAndYou',
            defaultMessage: 'For {firstName} and you',
          },
          { firstName }
        )
      : intl.formatMessage({
          id: 'home.outstandingItems.healthRecords.forYourChildAndYou',
          defaultMessage: 'For your child and you',
        })
  }

  if (child) {
    return firstName
      ? intl.formatMessage(
          {
            id: 'home.outstandingItems.healthRecords.forChild',
            defaultMessage: 'For {firstName}',
          },
          { firstName }
        )
      : intl.formatMessage({
          id: 'home.outstandingItems.healthRecords.forYourChild',
          defaultMessage: 'For your child',
        })
  }

  return intl.formatMessage({
    id: 'home.outstandingItems.healthRecords.forYou',
    defaultMessage: 'For you',
  })
}

const OutstandingItemsCard: React.FC<Props> = ({ items, connectSubjects }) => (
  <section className="flex flex-col rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-lg shadow-gray-900/10">
    <OutstandingItemsHeader />
    <OutstandingItemRows items={items} connectSubjects={connectSubjects} />
  </section>
)

const OutstandingItemsHeader: React.FC = () => (
  <>
    <h2 className="text-2xl leading-9 font-semibold tracking-tight text-[#002C22]">
      <FormattedMessage id="home.outstandingItems.heading" defaultMessage="Outstanding items" />
    </h2>
    <p className="text-text-body mt-3 text-sm leading-6">
      <FormattedMessage
        id="home.outstandingItems.body"
        defaultMessage="Review the items below to answer outstanding questions or complete an action."
      />
    </p>
  </>
)

const OutstandingItemRows: React.FC<Props> = ({ items, connectSubjects }) => (
  <div className="mt-6 space-y-4">
    {items.length > 0 ? <ConsentItems items={items} /> : null}
    {connectSubjects.length > 0 ? <HealthRecordsItem subjects={connectSubjects} /> : null}
  </div>
)

const ConsentItems: React.FC<{ items: OutstandingSecondaryConsentItem[] }> = ({ items }) => (
  <div className="rounded-lg border border-[#E5E7EB] p-4">
    <ul className="list-disc space-y-3 pl-5 text-base leading-6 text-gray-900">
      {items.map((item) => (
        <li key={item.id}>{item.title}</li>
      ))}
    </ul>
    <div className="mt-4 flex justify-end">
      <Link to="/consents/manage" className={buttonClasses}>
        <FormattedMessage id="home.outstandingItems.reviewConsents" defaultMessage="Review secondary consents" />
      </Link>
    </div>
  </div>
)

const HealthRecordsItem: React.FC<{ subjects: HrcConnectSubject[] }> = ({ subjects }) => {
  const intl = useIntl()
  const label = getHealthRecordsLabel(intl, subjects)
  const needsAttention = subjects.some(hasHealthRecordsConnectionNeedingAttention)

  return (
    <div className="rounded-lg border border-[#E5E7EB] p-4">
      <div className="min-w-0">
        <span className="inline-flex w-fit rounded-full bg-[#EAF5F1] px-3 py-1 text-xs leading-4 font-medium text-[#006045]">
          {label}
        </span>
        <h3 className="mt-3 text-lg leading-7 font-semibold text-[#002C22]">
          <FormattedMessage
            id="home.outstandingItems.healthRecords.title"
            defaultMessage="Help the care team see the full picture"
          />
        </h3>
        <p className="text-text-body mt-2 text-sm leading-6">
          <FormattedMessage
            id="home.outstandingItems.healthRecords.body"
            defaultMessage="In just a few minutes, you can give the care team a fuller view of health history. That can help right away, and over time it could lead to an updated report."
          />
        </p>
        {needsAttention ? (
          <p className="bg-warning-100 text-warning-600 mt-4 rounded-lg px-3 py-2 text-sm leading-6 font-medium">
            <FormattedMessage
              id="home.outstandingItems.healthRecords.attention"
              defaultMessage="There are health record connections that need your attention."
            />
          </p>
        ) : null}
      </div>
      <div className="mt-4 flex justify-end">
        <Link to="/health-records/connect" className={buttonClasses}>
          <FormattedMessage id="home.outstandingItems.healthRecords.button" defaultMessage="Connect records now" />
        </Link>
      </div>
    </div>
  )
}

export default OutstandingItemsCard
